import { loadFavorites } from './local_storage_helper'
import { pickTranslatedName } from '../../utils/events_utils'

const normalizeCutLineButton = (button) =>
  button && {
    state: button.state,
    shown: button.shown,
    cutListId: button.cut_list_id,
  };

// reason is an i18n key (translated client-side), not display text
const normalizePointsAndPurseButton = (button) =>
  button && {
    state: button.state,
    shown: button.shown,
    reason: button.reason,
  };

// Manager-only display-options warning: ordered {key, params?} translation
// descriptors, one tooltip line each (absent on portal payloads).
const normalizeInfoTooltip = (messages) =>
  (Array.isArray(messages) ? messages : [])
    .filter((message) => message?.key)
    .map((message) => ({ key: message.key, params: message.params ?? {} }));

// Builds the store event explicitly (no spread of the raw API object): only
// the fields the frontend consumes, all camelCase. The event name resolves to
// the viewer-locale translation (raw-name fallback) on every site - the
// manager site translates too, by product decision (2026-08-28), diverging
// from legacy replace_with_translated_name's manager-mode raw name.
const normalizeEvent = (rawEvent) => ({
  id: rawEvent.id,
  name: pickTranslatedName(rawEvent.name_translations, rawEvent.name),
  snapshots: rawEvent.snapshots ?? [],
  adjusted: rawEvent.adjusted,
  tournamentSpecId: rawEvent.tournament_spec_id,
  tournamentSpecName: rawEvent.tournament_spec_name,
  tournamentSpecType: rawEvent.tournament_spec_type,
  hasExpandableScorecards: rawEvent.has_expandable_scorecards,
  totalPurseAllocated: rawEvent.total_purse_allocated,
  totalPointsAllocated: rawEvent.total_points_allocated,
  handicapFormat: rawEvent.handicap_format,
  inProgress: rawEvent.in_progress,
  pointsAndPurseButton: normalizePointsAndPurseButton(rawEvent.points_and_purse_button),
  cutLineButton: normalizeCutLineButton(rawEvent.cut_line_button),
  infoTooltip: normalizeInfoTooltip(rawEvent.info_tooltip),
});

export const normalizeEvents = (arr = []) => {
  return arr.reduce((acc, item) => {
    // The tournaments endpoint now renders each event flat under `tournaments`
    // (include_child_root = false); older shapes wrapped it as `{ event: {...} }`.
    const rawEvent = item?.event ?? item;
    const key = rawEvent?.tournament_spec_id;

    if (!rawEvent?.id || !key) return acc;

    if (!acc[key]) acc[key] = [];
    acc[key].push(normalizeEvent(rawEvent));

    return acc;
  }, {});
};

/**
 * An algorithm that determines the which event to keep when switching on landscape/mobile view.
 * 
 * The Leaderboard can be one list (Events without favorites) or 
 * split in two lists (Events with favorites / Events without favorites).
 * 
 * The algorithm will choose the event to keep in this priority order:
 * 1. First opened event from Events with favorites.
 * 2. First opened event from Events without favorites.
 * 3. First event from Events with favorites.
 * 4. First event from Events without favorites.
 *
 * When an event has snapshots, the snapshot id matching the current round index
 * is used instead of the event id. Falls back to the event id if no match.
 *
 * Returns:
 * - openEventId: the first open event on the page (if exists);
 * - eventIdToKeep: the event to keep ( = openEventId if exists);
 */
export const findEventToKeep = (eventsList, eventSliceState, currentRoundIndex) => {
  let fallbackId;
  let firstFavorite;

  const favoritesIds = Object.keys(loadFavorites());

  // For each event, use the snapshot id matching the current round index if snapshots exist,
  // otherwise fall back to the event id. The parent id is kept alongside because
  // favorites buckets are keyed by it, never by a snapshot id (see getParentId).
  const eventEntries = Object.values(eventsList)
    .flat()
    .map((obj) => {
      if (obj.snapshots?.length > 0) {
        const matchingSnapshot = obj.snapshots.find(
          (snapshot) => snapshot.index === currentRoundIndex
        );
        return { id: matchingSnapshot?.id ?? obj.id, parentId: obj.id };
      }
      return { id: obj.id, parentId: obj.id };
    });

  // first open event in favorites or first open event
  const openEventId =
    eventEntries.find(({ id, parentId }) => {
      const event = eventSliceState?.[id];

      // first event in favorites
      if (!firstFavorite && favoritesIds.includes(parentId)) firstFavorite = id;

      if (!event) return false;

      // first open event
      if (!fallbackId) fallbackId = id;

      // first open event in favorites
      return (event.favorites?.length ?? 0) > 0;
    })?.id ?? fallbackId;

  // first event in favorites or first event
  const firstEventId = firstFavorite ?? eventEntries[0]?.id;

  const eventIdToKeep =
    openEventId && eventEntries.some((entry) => entry.id === openEventId)
      ? openEventId
      : firstEventId;

  return { eventIdToKeep, openEventId };
};

/*
 * Round-change reopen, capture side. The event slice is keyed by the round's
 * snapshot id when the event has snapshots, so every open key is normalized
 * back to its list event here, while the outgoing round's eventsList is still
 * in the store — the old snapshot id means nothing once the round changes.
 * Keys that map to no event (e.g. a spec-group row) are skipped.
 *
 * Returns [{ eventId, specId, name }] for each open event.
 */
export const collectOpenEventRecords = (eventsList, eventSliceState) => {
  const flatEvents = Object.values(eventsList ?? {}).flat();

  return Object.keys(eventSliceState ?? {}).reduce((records, openKey) => {
    const event =
      flatEvents.find((e) => String(e.id) === String(openKey)) ??
      flatEvents.find((e) => e.snapshots?.some((s) => String(s.id) === String(openKey)));

    if (event) {
      records.push({ eventId: event.id, specId: event.tournamentSpecId, name: event.name });
    }
    return records;
  }, []);
};

/*
 * Round-change reopen, match side. An event "found in the new round" is one
 * with the same id (multi-round tournaments keep their event id across
 * rounds — snapshots are resolved again at fetch time) or, for events
 * generated per round, one from the same tournament spec with the same name.
 *
 * Returns the matched event ids in the new list's display order.
 */
export const matchEventsToReopen = (records, newEventsList) => {
  if (!records.length) return [];

  const recordIds = new Set(records.map((r) => String(r.eventId)));
  const specNameKeys = new Set(records.map((r) => `${r.specId}|${r.name}`));

  return Object.values(newEventsList ?? {})
    .flat()
    .filter(
      (e) => recordIds.has(String(e.id)) || specNameKeys.has(`${e.tournamentSpecId}|${e.name}`)
    )
    .map((e) => e.id);
};
